import { z } from 'zod';
import { INDIA_LOCALE, PRICE_DECIMAL_PLACES, PRICE_FRESH_SECONDS } from '../config.ts';
import { MINUTE_MS } from './when.ts';

const ROUNDING_FACTOR = 10 ** PRICE_DECIMAL_PLACES;

export function round2(n: number): number {
  return Math.round(n * ROUNDING_FACTOR) / ROUNDING_FACTOR;
}

// Targets fire on a `touch` (one print); stops on a five-minute `close`, so one stray print cannot book a loss.
export const PRICE_BASES = ['touch', 'close'] as const;
type PriceBasis = (typeof PRICE_BASES)[number];

export interface Print {
  price: number;
  basis: PriceBasis;
}

export function touched(price: number): Print {
  return { price, basis: 'touch' };
}

export const TRADE_TYPES = ['LONG', 'SHORT'] as const;
export type TradeType = (typeof TRADE_TYPES)[number];

export function signOf(tradeType: TradeType): 1 | -1 {
  return tradeType === 'LONG' ? 1 : -1;
}

export function isLong(tradeType: TradeType): boolean {
  return signOf(tradeType) === 1;
}

export function wholeLots(units: number, lotSize: number): number {
  return Math.floor(units / lotSize);
}

export function toWholeLotUnits(units: number, lotSize: number): number {
  return wholeLots(units, lotSize) * lotSize;
}

// Integer paise: float `price / tick` is inexact (2850.53 / 0.05 is not 57010.6).
function snap(price: number, tickSize: number, round: (ticks: number) => number): number {
  const grid = Math.round(tickSize * ROUNDING_FACTOR);
  if (grid <= 1) return round2(price);
  const paise = Math.round(price * ROUNDING_FACTOR);
  // Never snap to zero: a zero limit behaves as a market order.
  return (Math.max(round(paise / grid), 1) * grid) / ROUNDING_FACTOR;
}

// Buy snaps down, sell up, so an order never pays more or takes less than named.
function toTick(price: number, tickSize: number, side: 'BUY' | 'SELL'): number {
  return snap(price, tickSize, side === 'BUY' ? Math.floor : Math.ceil);
}

// Decimals by magnitude, no grouping; `fmtPrice` uses Indian grouping, wrong for COMEX/NYMEX quotes.
export function fmtReference(value: number): string {
  const places = Math.abs(value) < 10 ? 3 : 2;
  return value.toFixed(places);
}

// Display only. Prices sent to a broker must use the directional `toTick`.
export function toNearestTick(price: number, tickSize: number): number {
  return snap(price, tickSize, Math.round);
}

// A long's levels snap down, a short's up: an entry never pays more than named, a stop never
// fires early and a target never asks more.
export function toTickFor(price: number, tickSize: number, tradeType: TradeType): number {
  return toTick(price, tickSize, tradeType === 'LONG' ? 'BUY' : 'SELL');
}

export function perUnitMove(
  tradeType: 'LONG' | 'SHORT',
  entryPrice: number,
  price: number,
): number {
  return signOf(tradeType) * (price - entryPrice);
}

const wholeNumberFormat = new Intl.NumberFormat(INDIA_LOCALE, { maximumFractionDigits: 0 });

export const fmtWhole = (value: number): string => wholeNumberFormat.format(value);

const BARE_UNITS = 'qty';

export function tradesInLots(lotSize: number): boolean {
  return lotSize > 1;
}

export function unitWord(lotSize: number, count: number): string {
  if (!tradesInLots(lotSize)) return BARE_UNITS;
  return count === 1 ? 'lot' : 'lots';
}

export function fmtSize(quantity: number, lotSize: number): string {
  if (!tradesInLots(lotSize)) return `${fmtWhole(quantity)} ${BARE_UNITS}`;
  const lots = wholeLots(quantity, lotSize);
  return `${lots} ${unitWord(lotSize, lots)}`;
}

export function fmtQuantity(quantity: number, lotSize: number): string {
  const size = fmtSize(quantity, lotSize);
  return tradesInLots(lotSize) ? `${fmtWhole(quantity)} ${BARE_UNITS} · ${size}` : size;
}

export const fmtCount = (count: number): string => count.toLocaleString(INDIA_LOCALE);

/** "A, B and C". */
/** Text matched as itself inside a regular expression. */
export const escapeRegex = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const andList = (names: readonly string[]): string =>
  names.length < 2 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;

/** A day's broker P&L net of its charges, F&O and commodity together. */
export const dayNet = (day: {
  fno: number | null;
  commodity: number | null;
  charges: number | null;
}): number => (day.fno ?? 0) + (day.commodity ?? 0) - (day.charges ?? 0);

export const counted = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`;

export function fmtRupees(value: number): string {
  return `₹${fmtWhole(value)}`;
}

/** U+2212 minus aligns with digits where '-' does not. */
export const signed = (value: number, say: (magnitude: number) => string): string =>
  `${value < 0 ? '−' : '+'}${say(Math.abs(value))}`;

export const fmtPercent = (fraction: number, places = 1): string =>
  `${(fraction * 100).toFixed(places)}%`;

/** Of a figure already in percent. */
export const fmtSignedPct = (
  percent: number | null | undefined,
  places: number = PRICE_DECIMAL_PLACES,
): string =>
  percent === null || percent === undefined
    ? '—'
    : signed(percent, (size) => `${size.toFixed(places)}%`);

export function fmtSignedRupees(value: number): string {
  return signed(value, fmtRupees);
}

const roundTo = (value: number, places: number) => Math.round(value * 10 ** places) / 10 ** places;

export function fmtR(value: number, places = 1): string {
  const rounded = roundTo(value, places);
  return `${rounded < 0 ? '−' : ''}${Math.abs(rounded)}R`;
}

// Round before signing so −0.04 prints as "0R", not "−0R".
export function fmtSignedR(value: number, places = 1): string {
  const rounded = roundTo(value, places);
  return rounded < 0
    ? `−${fmtR(-rounded, places)}`
    : rounded > 0
      ? `+${fmtR(rounded, places)}`
      : '0R';
}

export function fmtPrice(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString(INDIA_LOCALE, {
    minimumFractionDigits: PRICE_DECIMAL_PLACES,
    maximumFractionDigits: PRICE_DECIMAL_PLACES,
  });
}

// Drops `.00` only when told no sibling figure in the set has paise, so a set is never spelt two ways.
export const fmtLevel = (value: number, paise = false): string =>
  paise || !Number.isInteger(value) ? fmtPrice(value) : fmtWhole(value);

/** In R where there is one; a percent once the stop no longer measures a loss. */
export const fmtGain = ({ r, pct }: { r: number | null; pct: number }): string =>
  r !== null ? fmtSignedR(r) : Math.abs(pct) < 0.005 ? 'Flat' : fmtSignedPct(pct);

// `now` must be the server clock or the browser's server-synced one, never a raw local clock.
export function isFreshQuote(asOf: number | string | Date, now: number): boolean {
  return now - new Date(asOf).getTime() < PRICE_FRESH_SECONDS * 1000;
}

const CLOSE_BASIS_MINUTES = 5;

// A longer gap between ticks means the market was shut, not quiet.
const CLOSE_GAP_MAX_MINUTES = 10;

export function candleStillCloses(asOf: number | string | Date, now: number): boolean {
  return now - new Date(asOf).getTime() <= CLOSE_GAP_MAX_MINUTES * MINUTE_MS;
}

const CANDLE_MS = CLOSE_BASIS_MINUTES * MINUTE_MS;

export function bucketOf(at: number | string | Date): number {
  return Math.floor(new Date(at).getTime() / CANDLE_MS);
}

export function candleEndsAt(at: number | string | Date): number {
  return (bucketOf(at) + 1) * CANDLE_MS;
}

// An explicit `undefined` key is not the same as an absent one to drizzle `set` or zod.
export function defined<T extends object>(fields: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
}

export const idSchema = z.number().int().positive();

export const priceSchema = z.number().finite().positive().transform(round2);

// Key by a string or number: a Map compares objects by identity.
export function groupsOf<K, T>(rows: readonly T[], keyOf: (row: T) => K): Map<K, [T, ...T[]]> {
  const groups = new Map<K, [T, ...T[]]>();
  for (const row of rows) {
    const key = keyOf(row);
    const bucket = groups.get(key);
    if (bucket) bucket.push(row);
    else groups.set(key, [row]);
  }
  return groups;
}

const byKey = (a: string | number, b: string | number): number =>
  typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b));

export function groupBy<T, K extends string | number>(
  rows: readonly T[],
  keyOf: (row: T) => K,
): [K, [T, ...T[]]][] {
  return [...groupsOf(rows, keyOf)].sort((a, b) => b[1].length - a[1].length || byKey(a[0], b[0]));
}
